/**
 * FieldMarkings
 *
 * The yardage reference on the simulator field.
 *
 * ## What this replaces
 *
 * Three `<div className="yard-line">` elements laid out by `justify-content: space-evenly`, plus one
 * line of scrimmage. Because they were spaced by flexbox rather than positioned at yardages, they
 * meant nothing: they moved when the container's aspect ratio changed at the 600px breakpoint, and
 * nothing on the field mapped a distance on screen to a distance in yards.
 *
 * For a tool whose whole purpose is teaching route depth and coverage leverage, that was the most
 * consequential gap in the UI — a coach could not tell whether a curl was breaking at 8 yards or 14.
 *
 * ## The coordinate system
 *
 * Positions are percentages of the container, matching how AnimationEngine places players.
 * From the engine's model (see .kiro/steering/reaction-engine.md):
 *
 *   - `y = 0` is deep downfield, `y = 100` is behind the offence. LOWER y is DEEPER.
 *   - The line of scrimmage is `y = 85`.
 *   - 1 yard = 3 y-units, so 10 yards downfield is `y = 55`.
 *
 * Every line below is therefore derived from the LOS rather than hard-coded, so the markings and the
 * simulation agree by construction. If `LOS_Y` or `UNITS_PER_YARD` ever change in the engine, change
 * them here and the picture stays honest.
 *
 * ## A known distortion, deliberately not hidden
 *
 * The model is square (100x100) but the container is `14/10` on desktop and `10/12` on mobile, so a
 * yard downfield and a yard sideways are not the same number of screen pixels. These lines are drawn
 * in model space, which means the DEPTH scale they show is correct — that is the one a coach reads.
 * Lateral distance is still stretched. Widening the model is the real fix and is a much larger
 * change; see the steering doc.
 */

import './FieldMarkings.css';

/** Where the line of scrimmage sits, in container percent. Matches the engine's y = 85. */
const LOS_Y = 85;
/** Model units per yard, from the engine. 3 units = 1 yard, and the container is 100 units tall. */
const UNITS_PER_YARD = 3;

/** Depth in yards from the LOS to a container `top` percentage. Downfield is negative y. */
const yardsToTop = (yards: number) => LOS_Y - yards * UNITS_PER_YARD;

/**
 * Which depths get a line. Every 5 yards, labelled, out to the deepest depth the field shows.
 *
 * 25 yards lands at y = 10, near the top edge — deep enough to cover every route in the tree
 * (the deepest, a Go, tops out around 22 yards).
 */
const YARD_MARKS = [5, 10, 15, 20, 25];

/**
 * Hash marks, as a percentage of the field width.
 *
 * Real hashes sit inside the numbers; the exact inset differs between NFL and college. These are a
 * middle setting that reads correctly without claiming to be either.
 */
const HASH_X = [37, 63];

function FieldMarkings() {
  return (
    // aria-hidden: this is decoration for a sighted reader. The depths it communicates are also
    // available as text in the play summary, so hiding it removes noise rather than information.
    <div className="field-markings" aria-hidden="true">
      {/* Mown banding. Purely visual, and the reason the turf reads as grass rather than a green box. */}
      <div className="field-turf" />

      {/* Sidelines. The field used to have a 4px white border and 20px rounded corners, which read
          as a UI card; boundaries belong inside the playing surface. */}
      <div className="field-sideline field-sideline-left" />
      <div className="field-sideline field-sideline-right" />

      {YARD_MARKS.map((yards) => {
        const top = yardsToTop(yards);
        return (
          <div className="field-yard-group" key={yards}>
            <div className="field-yard-line" style={{ top: `${top}%` }} />
            {/* Numbers on both sides, as on a real field, so the depth is readable wherever the
                play is happening. */}
            <span className="field-yard-number field-yard-number-left" style={{ top: `${top}%` }}>
              {yards}
            </span>
            <span className="field-yard-number field-yard-number-right" style={{ top: `${top}%` }}>
              {yards}
            </span>
            {HASH_X.map((x) => (
              <div
                className="field-hash"
                key={x}
                style={{ top: `${top}%`, left: `${x}%` }}
              />
            ))}
          </div>
        );
      })}

      {/* Intermediate hash ticks at each yard between the 5s, which is what gives the eye a fine
          depth scale rather than just five stripes. */}
      {Array.from({ length: 25 }, (_, i) => i + 1)
        .filter((yards) => yards % 5 !== 0)
        .map((yards) => (
          <div
            className="field-yard-tick"
            key={`tick-${yards}`}
            style={{ top: `${yardsToTop(yards)}%` }}
          />
        ))}

      {/* The line of scrimmage, last so it sits above the yard lines. */}
      <div className="field-los" style={{ top: `${LOS_Y}%` }} />
    </div>
  );
}

export default FieldMarkings;
